import { BUILT_GROUPS } from "@/lib/built-catalogue";
import { resolveCaseStudyRef } from "@/lib/case-studies";

/**
 * WHAT OWNLY CAN BUILD, ARRANGED SO SOMEBODY CAN FIND THEIR OWN PROBLEM IN IT.
 *
 * ────────────────────────────────────────────────────────────────────────────────
 *  THE DISTINCTION THIS FILE EXISTS TO KEEP: a service is what we CAN build. A
 *  case study and a `/built` entry are what we HAVE built. They are different
 *  claims and they are stored in different fields, so a page cannot quietly turn
 *  one into the other.
 * ────────────────────────────────────────────────────────────────────────────────
 *
 * `canBuild` is capability. It may describe work we have not sold as a standalone
 * project, because the technical capability is real and a buyer is entitled to know
 * we can do it. `builtIds` and `caseStudySlugs` are evidence, and every value in
 * them is validated below against the real catalogue and the real case studies. A
 * typo fails the build rather than rendering a proof section with a hole in it.
 *
 * WHY A REGISTRY AND NOT ELEVEN HAND-WRITTEN PAGES. The same reasoning as
 * `sectors.ts`. These pages point at `/built` entries, at case studies, at each
 * other and at industries, and every one of those relationships would otherwise be
 * written down twice and rot on the second copy. Declared once, adding a service
 * wires its hub card, its route, its sitemap entry, its breadcrumb and its links in
 * both directions, and `check-definition-of-done` sees an unknown id immediately.
 *
 * TWO SERVICES DELIBERATELY HAVE NO PAGE HERE, and that is not an oversight.
 * `/custom-software-for-businesses` and `/business-automation-systems` already
 * exist, are substantial, and have whatever search equity they have earned. Giving
 * them a second home under `/services/` would be two pages competing for one
 * query, which is the cannibalisation this architecture is supposed to prevent. So
 * they appear in the taxonomy with their real `href` and no `page`, and the route
 * generator skips them.
 *
 * THE BAR FOR A PAGE OF ITS OWN: somebody searching for exactly this thing, landing
 * here and reading nothing else, gets a substantial and genuinely different answer.
 * Where two candidates would have said 80% of the same thing, they were merged and
 * the merge is recorded in the comment on the survivor. That list is in the report,
 * not invented later: dashboards and reporting sit inside internal systems, MVP and
 * product development sit inside SaaS, third-party integrations and API work sit in
 * one page, and legacy replacement is a section of the custom software page rather
 * than a page that would have to be written entirely in the conditional.
 */

export type ServiceCategorySlug =
  | "software-development"
  | "business-systems"
  | "automation-and-integration"
  | "web-and-commerce";

export type ServiceCategory = {
  slug: ServiceCategorySlug;
  name: string;
  /** One line on the hub, above the services in this category. */
  blurb: string;
};

export type ServicePage = {
  /** `<h1>`. Written as the thing, not as a keyword. */
  heading: string;
  /** `<title>`. Names the service in the words somebody searches for. */
  metaTitle: string;
  metaDescription: string;
  /** The paragraph under the heading. Opens on the owner's problem, in their words, in second person. */
  intro: string;
  /** What the thing actually is, for somebody who is not sure they are in the right place. */
  whatItIs: readonly string[];
  /**
   * "Sound familiar?" moments. Short fragments the owner will recognise from their own week,
   * written as symptoms rather than as an assertion about this reader's business.
   */
  signs: readonly string[];
  /** CAPABILITY. Things we can build. Not a claim that any of them has been sold before. */
  canBuild: readonly string[];
  /** How the work runs, in the terms this service actually changes. */
  approach: readonly string[];
  /** EVIDENCE. Ids from the built catalogue. Validated below. */
  builtIds: readonly string[];
  /** EVIDENCE. Case study slugs, or `slug#website` for a build's website on its own. Validated below. Empty is allowed and is honest. */
  caseStudySlugs: readonly string[];
  /** Services worth reading next. Validated below. */
  relatedSlugs: readonly string[];
  /**
   * Questions this service page answers, rendered on the page and marked up as FAQPage.
   *
   * Optional, and it should stay that way. A service page earns questions when search sends it
   * people who arrived with one, which is not every service. Where it does, answering them is
   * what turns a result at the bottom of page one into a click: an unadorned blue link there
   * gets ignored, and the questions are also the part an answer engine can quote.
   *
   * Google's rule: every answer must be visible on the page and complete on its own. The
   * template renders these, so using the field satisfies that.
   */
  faqs?: readonly { question: string; answer: string }[];
};

export type Service = {
  /** Identity, and the last path segment for services that get a generated page. */
  slug: string;
  /** Where the service actually lives. Not derived, because two of them predate this file. */
  href: `/${string}`;
  /** Hub card and nav label. The noun somebody would use for it. */
  name: string;
  category: ServiceCategorySlug;
  /** One line under the name on the hub. */
  hubBlurb: string;
  /** Absent for the two services that already have their own page elsewhere. */
  page?: ServicePage;
};

export const SERVICE_CATEGORIES: readonly ServiceCategory[] = [
  {
    slug: "software-development",
    name: "Software development",
    blurb: "When what you need doesn't exist yet, we build it.",
  },
  {
    slug: "business-systems",
    name: "Business systems",
    blurb:
      "The system your business runs on day to day: jobs, customers, staff and what happens next.",
  },
  {
    slug: "automation-and-integration",
    name: "Automation and integration",
    blurb:
      "The jobs someone does by hand every week, done by your own system instead.",
  },
  {
    slug: "web-and-commerce",
    name: "Web and commerce",
    blurb:
      "The site people judge you on, and the way they buy, book and join.",
  },
];

/*
 * THE COPY IS IN THE OWNER'S VOICE (Tom, 2026-10-30: "speak into the actual business owner and
 * get them to say yes in their head", and "just quite a lot of text"). Each intro opens on a
 * moment the owner will recognise, in second person, then says what we build. Lists are kept to
 * the strongest few items. The approved reference is the homepage "Sound familiar?" section.
 */
export const SERVICES: readonly Service[] = [
  /* ── Software development ─────────────────────────────────────────────── */
  {
    slug: "custom-software-development",
    /* Lives where it already lives. See the note at the top of this file. */
    href: "/custom-software-for-businesses",
    name: "Custom software development",
    category: "software-development",
    hubBlurb:
      "Software built around how you already work, not a tool you bend yourself to fit.",
  },
  {
    slug: "web-app-development",
    href: "/services/web-app-development",
    name: "Web application development",
    category: "software-development",
    hubBlurb: "Your team's work in one place, open in any browser.",
    page: {
      heading: "Web application development",
      metaTitle: "Web application development in the UK | Ownly Digital",
      metaDescription:
        "We build web applications for UK businesses: the software your team works in every day, in a browser, on your own data, built around how you work.",
      intro:
        "Still running the business off a spreadsheet only one person understands? A web app gives your team one place to do the work, open in any browser, built around how you already work.",
      whatItIs: [
        "Logins, your own data, and screens built for the jobs your people actually do. Rules stop the wrong thing being saved, and everyone sees the same thing at the same time.",
      ],
      signs: [
        "One spreadsheet, and only one person who really gets it.",
        "Two people editing the same row, neither sure whose version is right.",
        "A step that only happens because someone remembers.",
        "Asking three people to find out where a job is up to.",
      ],
      canBuild: [
        "Screens that follow the job in the order it happens",
        "Logins and permissions, so people only see what's theirs",
        "A record of who changed what, and when",
        "Search across everything, and changes to lots of records in one go",
        "Notifications that reach the person who has to act",
      ],
      approach: [
        "We start from the work, not the screens: what happens now, who touches it, and where it goes wrong.",
        "The first release fixes the part that costs you the most time. What comes next is decided by the people using it.",
      ],
      builtIds: [
        "live-team-board",
        "a-days-work-decided-for-you",
        "customers-grouped-how-you-work",
        "ie-audit-trail",
        "ie-safe-csv",
        "numbers-that-cannot-disagree",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "internal-business-systems",
        "custom-software-development",
        "integrations-and-apis",
      ],
    },
  },
  {
    slug: "mobile-app-development",
    href: "/services/mobile-app-development",
    name: "Mobile app development",
    category: "software-development",
    hubBlurb:
      "Your own app on iPhone and Android, for work that happens away from a desk.",
    page: {
      heading: "Mobile app development",
      metaTitle: "Mobile app development for iOS and Android | Ownly Digital",
      metaDescription:
        "iOS and Android app development for UK businesses. We build the app, take it through Apple and Google review, and look after it once it is live.",
      intro:
        "Your customers are in the gym, on site or on the move, and your messages sit unread in their inbox. Your own app puts you on their home screen.",
      whatItIs: [
        "An app earns its place when it does what a website can't: work on a bad signal, use the camera properly, and send a notification that actually arrives.",
      ],
      signs: [
        "Customers on their feet all day, not sat at a desk.",
        "Important messages read a day late, if at all.",
        "Photos and videos sent over WhatsApp as part of the job.",
        "Paying for a platform that puts its brand in front of your clients.",
      ],
      canBuild: [
        "One app for both the App Store and Google Play",
        "Push notifications that land on the phone",
        "Photo and video uploads that wait for signal instead of failing",
        "Accounts, subscriptions and in-app payments where the stores need them",
        "The store listing, the review and every resubmission",
      ],
      approach: [
        "The store rules shape what gets built, so we settle them at the start, not the week before launch. We know what reviewers object to, and we plan around it.",
      ],
      builtIds: [
        "video-that-uploads-from-a-phone",
        "notification-that-lands-on-the-phone",
        "pf-haptics",
        "progress-photo-compare",
        "pf-fresh-on-return",
        "gs-get-app-redirect",
      ],
      caseStudySlugs: ["proflow-academy", "proflow-physiques", "growthsync"],
      relatedSlugs: [
        "memberships-and-subscriptions",
        "saas-and-software-products",
        "client-portals",
      ],
    },
  },
  {
    slug: "saas-and-software-products",
    href: "/services/saas-and-software-products",
    name: "SaaS and software products",
    category: "software-development",
    hubBlurb:
      "When the software is what you sell. A first version people will pay for.",
    page: {
      heading: "SaaS and software product development",
      metaTitle: "SaaS development and software products | Ownly Digital",
      metaDescription:
        "MVP and SaaS development for founders turning an idea or an internal tool into a product people pay for. We run our own product, so we know the costs.",
      intro:
        "Customers keep asking if they can use the tool you built for yourselves? Turning it into a product is a different job, and we've done it with our own.",
      whatItIs: [
        "A product has to be signed up for by a stranger, understood without training, paid for on a card and supported afterwards. The first version is the one loop that makes someone pay: sign up, get the value, come back.",
      ],
      signs: [
        "Customers asking if they can use your internal tool.",
        "A platform taking a cut of something you could own.",
        "An idea you've explained enough times to know people would pay.",
        "Needing something real in front of users before the full build.",
      ],
      canBuild: [
        "Sign-up, plans and billing",
        "A first release built around one loop real users can test",
        "Onboarding that gets people to the good part fast",
        "Analytics showing which features actually get used",
        "The admin side: support, refunds, data export and deletion",
      ],
      approach: [
        "We run GrowthSync, our own product, so we've already paid for the lessons on app store review, getting the first users in, and features nobody used.",
        "We'll push back on the feature list before we build. The most expensive part of a first version is the bit nobody needed.",
      ],
      builtIds: [
        "gs-product-analytics",
        "gs-honest-match-score",
        "gs-safety-controls",
        "gs-take-your-data",
        "gs-invite-your-people",
        "gs-host-free-event",
      ],
      caseStudySlugs: ["growthsync"],
      relatedSlugs: [
        "mobile-app-development",
        "web-app-development",
        "memberships-and-subscriptions",
      ],
    },
  },

  /* ── Business systems ─────────────────────────────────────────────────── */
  {
    slug: "internal-business-systems",
    href: "/services/internal-business-systems",
    name: "Internal business systems",
    category: "business-systems",
    hubBlurb:
      "One place the work lives, and a clear view of what's happening in it.",
    page: {
      heading: "Internal business systems",
      metaTitle: "Internal business systems and software | Ownly Digital",
      metaDescription:
        "Custom internal systems for UK businesses: jobs, workflows, dashboards and permissions, so the work lives in one place instead of a spreadsheet.",
      intro:
        "Can you say how many jobs are open right now without opening three tabs? Most businesses run on a spreadsheet, a shared inbox and someone's memory. It works, until you grow.",
      whatItIs: [
        "The software your own people work in: where every job lives from enquiry to invoice, who has it now, and what happens next. Your dashboards come from the same place, so the numbers finally agree.",
      ],
      signs: [
        "A weekly report someone pulls together by hand.",
        "Two versions of the same number, and an argument about which is right.",
        "Jobs missed at the handover between two people.",
        "New starters taking weeks, because the process lives in people's heads.",
      ],
      canBuild: [
        "A live board of every job and where it's up to",
        "A screen telling each person what today looks like",
        "Dashboards and weekly digests that arrive on their own",
        "Permissions, so a contractor sees their jobs and not your margins",
        "Alerts when something stops working, instead of silent failures",
      ],
      approach: [
        "We map what happens now, including the bits that only work because someone quietly covers them. Then the smallest useful system goes live on real work, and grows from there.",
      ],
      builtIds: [
        "live-team-board",
        "a-days-work-decided-for-you",
        "jobs-that-say-when-they-die",
        "something-looks-wrong-digest",
        "numbers-that-cannot-disagree",
        "ie-audit-trail",
        "new-starter-checklist",
        "training-that-stays-put",
        "job-finishes-itself",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "crm-development",
        "business-process-automation",
        "web-app-development",
      ],
    },
  },
  {
    slug: "crm-development",
    href: "/services/crm-development",
    name: "Custom CRM development",
    category: "business-systems",
    hubBlurb:
      "A CRM shaped like the way you sell, not one you bend to fit and pay for per seat.",
    page: {
      heading: "Custom CRM development",
      metaTitle: "Custom CRM development for UK businesses | Ownly Digital",
      metaDescription:
        "Custom CRM development: pipelines, contact records, quoting, automatic follow up and reporting, built around how you actually sell, not how a product thinks.",
      intro:
        "Quotes going out and nobody sure which ones were chased? Most CRMs die because keeping them up to date is someone's least favourite job. Yours should update itself.",
      whatItIs: [
        "Who you're talking to, what you've quoted and what happens next, in one place shaped like your sales process, whether that's a survey before a quote or a deposit before a slot. The record updates as the work happens, not when someone remembers to type.",
      ],
      signs: [
        "The real pipeline living in a spreadsheet.",
        "The same customer saved three times under three spellings.",
        "A lead that went quiet six months ago, never looked at again.",
        "The important part still happening in your inbox.",
      ],
      canBuild: [
        "A pipeline with your stages, and rules for what moves a deal on",
        "Quoting inside the CRM, priced the same way every time",
        "Follow-ups that go out whether or not anyone remembers",
        "One next action per customer, so the list is a to-do, not a filing cabinet",
        "Old customers brought back up on a schedule",
      ],
      approach: [
        "We start with your pipeline as it really runs, then decide what the system does by itself at each step. Your existing data comes across, duplicates merged, as part of the build.",
      ],
      builtIds: [
        "click-to-call-crm",
        "automatic-chasing",
        "which-deals-will-close",
        "one-next-action",
        "same-customer-twice",
        "dormant-customers-resurfaced",
        "replies-matched-automatically",
        "knows-its-a-bank-holiday",
        "customers-grouped-how-you-work",
        "who-has-not-replied-yet",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "quoting-and-estimating",
        "business-process-automation",
        "internal-business-systems",
      ],
    },
  },
  {
    slug: "client-portals",
    href: "/services/client-portals",
    name: "Client and customer portals",
    category: "business-systems",
    hubBlurb:
      "A login where your clients see their own job, their documents and their next step.",
    page: {
      heading: "Client portal development",
      metaTitle: "Client portal development | Ownly Digital",
      metaDescription:
        "Client portal development: a private login where clients see their job, their documents and what happens next, so they stop ringing to ask.",
      intro:
        "Fed up of answering \"where are we up to?\" all week? A client portal answers it before they ring.",
      whatItIs: [
        "A private login where each client sees their own job, their documents, anything waiting on them and what happens next. The best ones answer the question your team gets asked most.",
      ],
      signs: [
        "The same status question, from different clients, every week.",
        "Documents emailed back and forth until nobody knows which is latest.",
        "Onboarding a client by a chain of emails that stalls when someone's on holiday.",
        "Sensitive documents sent by email because it's the easy way.",
      ],
      canBuild: [
        "A login where each client sees their job and nobody else's",
        "Links to sign or upload without making an account",
        "Intake forms that file straight into your system",
        "E-signature, with the signed copy saved against the job",
        "Nudges when something is waiting on the client",
      ],
      approach: [
        "We decide first what each client is allowed to see, because that shapes everything else. Then we make the portal answer the one question your team hears most.",
      ],
      builtIds: [
        "client-portal",
        "token-link-documents",
        "documents-behind-a-gate",
        "client-intake-forms",
        "esign-retainer",
        "ie-feedback-link",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "internal-business-systems",
        "memberships-and-subscriptions",
        "web-app-development",
      ],
    },
  },
  {
    slug: "booking-systems",
    href: "/services/booking-systems",
    name: "Booking and scheduling systems",
    category: "business-systems",
    hubBlurb:
      "Bookings and deposits taken without a phone call, on your own site and in your own brand.",
    page: {
      heading: "Booking system development",
      metaTitle: "Booking system development | Ownly Digital",
      metaDescription:
        "Custom booking systems with deposits, reminders, availability rules and calendar sync, on your own site or in your own app, under your own brand.",
      intro:
        "Still taking bookings over the phone and writing them in the diary? Every booking that needs a call is capped by how many calls you can take.",
      whatItIs: [
        "Your customers pick a slot, pay a deposit and get a reminder, without anyone picking up the phone. The hard part isn't the calendar, it's your rules: who can be booked, when, at what price, and what happens when someone doesn't turn up.",
      ],
      signs: [
        "Bookings agreed in messages and copied into a calendar by hand.",
        "No-shows costing you, because nothing is taken up front.",
        "Customers sent to someone else's branded booking page.",
        "Part of every day spent sending reminders.",
      ],
      canBuild: [
        "Availability by person, service and day",
        "Deposits, balances and instalments taken at booking",
        "Booking on your own site or inside your own app, in your brand",
        "Confirmations and reminders that go out on their own",
        "Prepaid sessions, counted down automatically",
      ],
      approach: [
        "We write your rules out plainly first, because most booking problems are an edge case found after launch. Payments and the calendar are wired together from day one, so nothing needs matching up by hand.",
      ],
      builtIds: [
        "in-app-branded-booking",
        "multi-step-booking-sync",
        "prepaid-session-tokens",
        "academy-pick-your-coach-pricing",
        "academy-unavailable-coach-cannot-be-booked",
        "gs-calendar-sync",
        "enquiry-into-the-system-you-already-use",
        "book-you-inside-your-own-app",
        "physiques-never-invents-a-checkout",
      ],
      caseStudySlugs: ["proflow-academy", "the-dfc-experience"],
      relatedSlugs: [
        "quoting-and-estimating",
        "memberships-and-subscriptions",
        "client-portals",
      ],
    },
  },
  {
    slug: "quoting-and-estimating",
    href: "/services/quoting-and-estimating",
    name: "Quoting and estimating systems",
    category: "business-systems",
    hubBlurb:
      "Every job priced the same way, and a deposit taken while they're still keen.",
    page: {
      /**
       * Titled at what people type, which is not what we called this internally.
       * Search Console for 20 Jul to 16 Sep 2026: the demand on this topic is
       * "instant quote" (56 impressions), "instant software estimate" (41),
       * "instant quote tool" (24), "quote tool" (13), then a long tail of
       * "instant estimator", "quote estimator", "online instant quote". The phrase
       * "quoting and estimating system development" appears in none of it. The old
       * title used our words, so the page was invisible to its own buyers.
       */
      heading: "Instant quote tools and estimating systems",
      metaTitle: "Instant quote tools and estimating software | Ownly Digital",
      metaDescription:
        "Instant quote tools for your business: a price calculator on your own site, pricing rules that agree every time, and a deposit link at the moment of the yes.",
      intro:
        "Still typing quotes up at night? Then waiting days to hear back, and forgetting to chase the ones that go quiet. That's money lost twice.",
      whatItIs: [
        "Your pricing rules in one place, so every quote comes out the same whoever does it. Customers can get a real figure on your website, and an agreed price becomes a quote, a deposit link and a follow-up without anyone retyping it.",
      ],
      signs: [
        "Quotes going out two days after the enquiry.",
        "Two people pricing the same job differently.",
        "Finding out a quote was never chased when they buy elsewhere.",
        "A verbal yes that takes days to become a paid deposit.",
      ],
      canBuild: [
        "An instant price calculator on your own website",
        "Pricing rules that give the same number every time",
        "A quote document made from the agreed figure, not retyped",
        "A deposit link the moment they say yes",
        "Follow-ups that chase the quote and stop when they reply",
      ],
      approach: [
        "Getting your pricing out of your head and written down is most of the work, and it comes before any screens. That's what makes the same job get the same price every time.",
      ],
      builtIds: [
        "instant-quote-generator",
        "price-a-job-consistently",
        "scope-to-payment",
        "smart-intake-quiz",
        "project-qualification-quiz",
        "abandoned-enquiry-digest",
        "answer-first-lead-tool",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "crm-development",
        "business-process-automation",
        "booking-systems",
      ],
      /*
       * THIS PAGE INHERITED A RANKING, SO IT ANSWERS THE QUESTIONS THAT CAME WITH IT.
       *
       * `/work/instant-quote-estimator` used to carry these searches and now redirects here,
       * which is the right destination: the queries are "instant quote tool" and "quote tool",
       * and this is the page that sells one. But a redirect only moves the ranking. It does not
       * make the destination answer what the person typed.
       *
       * Search Console, 90 days to 2026-09-21, across the old page and this one: "instant
       * quote" at position 10, "instant software estimate" (43 impressions), "instant quote
       * tool", "instant online quote", "quote tool". Nought clicks between them.
       *
       * Two different people arrive on those searches and only one of them was being served.
       * Somebody who wants a quote tool BUILT is why this page exists. Somebody who wants a
       * price FOR THEMSELVES reached a page selling software with nothing telling them how to
       * get one, and the first answer closes that.
       *
       * IT DELIBERATELY DOES NOT POINT AT AN ESTIMATOR. This site used to run one at `/start`
       * and the case study about it was `/work/instant-quote-estimator`. Both now redirect,
       * `/start` to `/before-we-talk` and the study to this page, so the funnel is the briefing
       * and a call. Checked live on 2026-09-23 rather than assumed, because an answer promising
       * a costed range in two minutes would have been selling something that no longer exists.
       * If the estimator ever comes back, this answer is the first thing to update.
       *
       * The questions are kept word for word (they are what people search); the answers were
       * cut down on 2026-10-30 and still stand on their own, as Google's FAQ rule requires.
       */
      faqs: [
        {
          question: "Can I get a price for my own project without a call?",
          answer:
            "Partly. We price on what the software has to do, not how many pages it has, so the firm number comes from one conversation. Before that, there's a briefing you can watch in your own time, showing real finished work and what it cost, so you can judge the ballpark first.",
        },
        {
          question: "What is an instant quote tool?",
          answer:
            "A price calculator on your own website, driven by your pricing rules. A customer picks the options for their job and gets a real figure while they're still interested, instead of a form promising someone will be in touch.",
        },
        {
          question: "How accurate can an instant quote be?",
          answer:
            "As accurate as the pricing rules behind it. Most give a range rather than one figure, which is the honest answer at that stage: enough for a customer to decide to keep talking, with the firm number agreed once the detail is known.",
        },
        {
          question: "What does my business need before it can quote instantly?",
          answer:
            "Pricing someone can write down: what drives the number, which options change it, and where the limits are. If quotes come out of one person's head, writing that down is the first job, and we do it with you.",
        },
        {
          question: "Will an instant quote put customers off, or cost me negotiating room?",
          answer:
            "It filters, which is the point. People who were never going to afford it stop taking up your mornings, and the ones who carry on have already accepted the ballpark. A range keeps the final number where it belongs, in the conversation.",
        },
        {
          question: "Does this replace how we quote now, or sit alongside it?",
          answer:
            "Either. Some businesses want the calculator on the website for customers. Others want it for the team, so the same job always gets the same price and the agreed figure becomes a quote, a deposit link and a follow-up. Most end up with both.",
        },
      ],
    },
  },

  /* ── Automation and integration ───────────────────────────────────────── */
  {
    slug: "business-process-automation",
    /* Lives where it already lives. See the note at the top of this file. */
    href: "/business-automation-systems",
    name: "Business process automation",
    category: "automation-and-integration",
    hubBlurb:
      "The weekly jobs that only happen because someone remembers, happening on their own.",
  },
  {
    slug: "integrations-and-apis",
    href: "/services/integrations-and-apis",
    name: "Integrations and APIs",
    category: "automation-and-integration",
    hubBlurb:
      "Your own system working with your accounts, payments and anything else worth keeping.",
    page: {
      heading: "Software integrations and API development",
      metaTitle: "Software integrations and API development | Ownly Digital",
      metaDescription:
        "Integration work inside the systems we build: your software working with your accounts, payments and booking tools, plus APIs and webhooks where needed.",
      intro:
        "Typing the same details into two systems? Your own system should work with your accounts package and card payments, so nobody spends the day copying between them.",
      whatItIs: [
        "A booking is made, and it lands in the calendar. An invoice is paid, and it lands in your accounts. Every record agrees, and where something has no way in, we build one.",
      ],
      signs: [
        "The same details typed into two systems by the same person.",
        "A monthly export and import sat in someone's diary.",
        "Two systems that disagree, and nobody knows which is right.",
        "An automation that broke quietly and was found weeks later.",
      ],
      canBuild: [
        "Your system working with the software it needs, in both directions",
        "Changes that wait and retry if the other side is down, instead of getting lost",
        "Updates the moment something happens, not once an hour",
        "Your data moved across from a system you're leaving",
        "Alerts when a connection stops working",
      ],
      approach: [
        "We assume the other side will fail at some point, because it will. Retries and an alert to a real person are built in from the start, not after the week you lost data.",
      ],
      builtIds: [
        "enquiry-into-the-system-you-already-use",
        "gs-calendar-sync",
        "jobs-that-say-when-they-die",
        "replies-matched-automatically",
        "multi-step-booking-sync",
        "ie-safe-csv",
        "gs-take-your-data",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "business-process-automation",
        "internal-business-systems",
        "crm-development",
      ],
    },
  },

  /* ── Web and commerce ─────────────────────────────────────────────────── */
  {
    slug: "website-design-and-development",
    href: "/services/website-design-and-development",
    name: "Website design and development",
    category: "web-and-commerce",
    hubBlurb:
      "The site people judge you on, easy for you to edit and built to be found.",
    page: {
      heading: "Website design and development",
      metaTitle: "Website design and development in the UK | Ownly Digital",
      metaDescription:
        "Website design and development for UK businesses: fast, accessible sites you can edit yourself, a page for each thing people search for, one next step.",
      intro:
        "Website built years ago, and you can't change a thing without paying someone? Then it's working against you.",
      whatItIs: [
        "A website has one job: turn someone who's found you into an enquiry. That comes down to saying what you do in the words people search for, with a page for each thing you sell.",
      ],
      signs: [
        "Something you sell that isn't on the website at all.",
        "Search sending you almost nobody, apart from people typing your name.",
        "A site that looks fine on a laptop and fights you on a phone.",
        "Pages that end with no clear next step.",
      ],
      canBuild: [
        "A page for every service you sell and every area you work in",
        "Content you can edit yourself, without breaking anything",
        "Pages built to be found in Google and AI search",
        "Enquiry forms that stop bots and reach a person",
        "Fast, accessible pages on every phone",
      ],
      approach: [
        "Structure first, because that decides whether you get found. Then the words, then the design. And we hand over something you can run yourself.",
      ],
      builtIds: [
        "self-serve-cms",
        "a-page-for-every-place-you-work",
        "a-page-for-every-service-searched",
        "see-it-before-it-goes-live",
        "site-that-refreshes-itself",
        "mta-one-next-step-per-page",
        "mta-analytics-only-after-consent",
        "gs-search-landing-pages",
        "forms-that-bots-cannot-fill",
      ],
      caseStudySlugs: [
        "my-trusted-accountant",
        "proflow-academy#website",
        "the-dfc-experience",
      ],
      relatedSlugs: [
        "memberships-and-subscriptions",
        "booking-systems",
        "custom-software-development",
      ],
    },
  },
  {
    slug: "memberships-and-subscriptions",
    href: "/services/memberships-and-subscriptions",
    name: "Memberships and subscriptions",
    category: "web-and-commerce",
    hubBlurb:
      "Selling access, not hours: recurring payments, a members area and content released on your terms.",
    page: {
      heading: "Membership and subscription platforms",
      metaTitle: "Membership and subscription platforms | Ownly Digital",
      metaDescription:
        "Membership and subscription platforms: recurring payments, a members area, content released on a schedule and the admin behind it, owned by you.",
      intro:
        "Paying a platform a cut of every member, with their brand in front of yours? You could own it instead.",
      whatItIs: [
        "Your own platform handles who's paid, what they get, what happens when a payment fails, and how the content reaches them. On the web, or in your own app.",
      ],
      signs: [
        "A percentage of every payment going to a platform.",
        "Members seeing someone else's brand more than yours.",
        "Content released by hand because it can't be scheduled.",
        "Failed payments found out about late, or not at all.",
      ],
      canBuild: [
        "Plans, recurring payments, upgrades and failed payment handling",
        "A members area on the web or in your own app",
        "Content released on a schedule, or unlocked by progress",
        "Check-ins and progress tracking that bring people back each week",
        "Messages to one group of members, not everyone",
      ],
      approach: [
        "Keeping members matters more than signing them up, so we build what gives them a reason to open it in week six first. Failed payments, cancellations and refunds come early too, because they decide whether the revenue is real.",
      ],
      builtIds: [
        "drip-video-academy",
        "custom-checkin-builder",
        "message-a-slice-of-your-customers",
        "everything-you-know-in-one-library",
        "build-once-reuse-forever",
        "academy-checkout-without-a-basket",
        "unsubscribes-that-actually-work",
        "gs-take-your-data",
      ],
      caseStudySlugs: ["proflow-academy", "elevate-physique-coaching"],
      relatedSlugs: [
        "mobile-app-development",
        "booking-systems",
        "saas-and-software-products",
      ],
    },
  },
];

/**
 * EVERY REFERENCE IS CHECKED AT IMPORT, AND A BAD ONE STOPS THE BUILD.
 *
 * These pages make claims of two kinds and only one of them is safe to get wrong. A capability
 * sentence is ours to write. A proof link is a promise that a real thing exists behind it, and a
 * renamed built entry or a retired case study would otherwise turn that promise into an empty
 * section or a dead link, on the page whose whole job is to be checkable.
 *
 * Throwing here rather than filtering quietly is deliberate: a proof section that silently
 * shrinks is exactly the failure nobody notices.
 */
const BUILT_IDS = new Set(
  BUILT_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
);
const SERVICE_SLUGS = new Set(SERVICES.map((service) => service.slug));

for (const service of SERVICES) {
  const where = `services.ts: ${service.slug}`;
  if (!SERVICE_CATEGORIES.some((c) => c.slug === service.category)) {
    throw new Error(`${where} is in unknown category "${service.category}"`);
  }
  if (!service.page) continue;
  for (const id of service.page.builtIds) {
    if (!BUILT_IDS.has(id)) {
      throw new Error(`${where} cites built entry "${id}", which does not exist`);
    }
  }
  for (const slug of service.page.caseStudySlugs) {
    if (!resolveCaseStudyRef(slug)) {
      throw new Error(`${where} cites case study "${slug}", which does not exist`);
    }
  }
  for (const slug of service.page.relatedSlugs) {
    if (!SERVICE_SLUGS.has(slug)) {
      throw new Error(`${where} links to service "${slug}", which does not exist`);
    }
    if (slug === service.slug) {
      throw new Error(`${where} lists itself as a related service`);
    }
  }
}

/** The services that get a generated page under `/services/`. */
export const SERVICES_WITH_PAGES = SERVICES.filter(
  (service): service is Service & { page: ServicePage } => Boolean(service.page),
);

export function getService(slug: string): Service | undefined {
  return SERVICES.find((service) => service.slug === slug);
}

/** Services in a category, in declaration order, which is the order they are shown. */
export function servicesInCategory(category: ServiceCategorySlug): readonly Service[] {
  return SERVICES.filter((service) => service.category === category);
}

/** The built entries a service cites, resolved to the real items. */
export function builtItemsForService(page: ServicePage) {
  const items = BUILT_GROUPS.flatMap((group) =>
    group.items.map((item) => ({ ...item, group })),
  );
  return page.builtIds
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
}

/** The case studies a service cites, resolved. Empty means we have not delivered one yet. */
export function caseStudiesForService(page: ServicePage) {
  return page.caseStudySlugs
    .map((slug) => resolveCaseStudyRef(slug))
    .filter((study): study is NonNullable<typeof study> => Boolean(study));
}

/**
 * The reverse: the services that cite this case study.
 *
 * WHY THIS EXISTS. `caseStudySlugs` was only ever read in one direction, so a service page
 * pointed at its proof and the proof pointed nowhere back. `sectors.ts` already argues why that
 * rots: the pairing is written down once and the second half is the half nobody remembers to
 * add. Deriving it from the same registry means naming a case study on a service wires the link
 * both ways, and the validation above already guarantees the slug is real.
 *
 * IT IS ALSO THE SEO POINT. On 2026-09-13 `/work/instant-quote-estimator` was carrying
 * twenty-nine separate "instant quote" searches on its own, about 170 a month at positions 9 to
 * 15, while `/services/quoting-and-estimating` — the page that actually sells the thing those
 * people are looking for — was cited by it and linked from nowhere. The page with the rankings
 * was passing none of them to the page with the offer.
 *
 * Declaration order, so the first service listed is the primary one.
 */
export function servicesForCaseStudy(slug: string) {
  return SERVICES_WITH_PAGES.filter((service) =>
    service.page.caseStudySlugs.includes(slug),
  );
}

/**
 * The one service page a case study should send a reader to, or nothing.
 *
 * `primaryServiceSlug` on the study wins when it is set; otherwise the first service that cites
 * it, in declaration order. Throws if a study names a service that does not cite it back, so
 * the pairing cannot rot into a link that claims a relationship the registry does not have.
 */
export function primaryServiceForCaseStudy(study: {
  slug: string;
  primaryServiceSlug?: string;
}) {
  const candidates = servicesForCaseStudy(study.slug);
  if (!study.primaryServiceSlug) return candidates[0];
  const named = candidates.find(
    (service) => service.slug === study.primaryServiceSlug,
  );
  if (!named) {
    throw new Error(
      `Case study "${study.slug}" names primary service "${study.primaryServiceSlug}", which does not list it in caseStudySlugs`,
    );
  }
  return named;
}
